import { ref, readonly } from 'vue'

export type ToastKind = 'info' | 'success' | 'warning' | 'error'

/** Inline action rendered as a button inside the toast (e.g. "Undo"). */
export interface ToastAction {
  label: string
  handler: () => void
}

export interface Toast {
  id: number
  kind: ToastKind
  title: string
  caption?: string
  action?: ToastAction
}

export interface ToastOptions {
  caption?: string
  action?: ToastAction
  /**
   * Auto-dismiss delay in ms. Defaults to `DEFAULT_TIMEOUT_MS`. Pass `null` to
   * keep the toast until it is dismissed explicitly — used by the undo-approval
   * toast, whose lifetime is owned by the store's commit timer, not the queue.
   */
  timeoutMs?: number | null
}

const toasts = ref<Toast[]>([])
const timeouts = new Map<number, number>()
let nextId = 1
const DEFAULT_TIMEOUT_MS = 5_000
const MAX_TOASTS = 5

function push(kind: ToastKind, title: string, options: ToastOptions = {}): number {
  const id = nextId++
  toasts.value.push({ id, kind, title, caption: options.caption, action: options.action })
  // FIFO eviction: cap the queue so a burst of failures (e.g. BO outage during
  // rapid drags) can't grow unbounded — each toast holds a timeout and
  // a DOM node.
  while (toasts.value.length > MAX_TOASTS) {
    const evicted = toasts.value.shift()
    if (evicted) clearTimeoutFor(evicted.id)
  }
  const timeoutMs = options.timeoutMs === undefined ? DEFAULT_TIMEOUT_MS : options.timeoutMs
  if (timeoutMs !== null) {
    const handle = window.setTimeout(() => dismiss(id), timeoutMs)
    timeouts.set(id, handle)
  }
  return id
}

function clearTimeoutFor(id: number) {
  const handle = timeouts.get(id)
  if (handle !== undefined) {
    window.clearTimeout(handle)
    timeouts.delete(id)
  }
}

function dismiss(id: number) {
  clearTimeoutFor(id)
  const i = toasts.value.findIndex((t) => t.id === id)
  if (i !== -1) toasts.value.splice(i, 1)
}

/**
 * Minimal global toast queue.
 *
 * One module-scoped queue keeps the API simple: any component or store can
 * `push` without prop-drilling a host ref. The single mounted `ToastHost`
 * organism renders the list via `Carbon`'s `CvToastNotification`. Timeouts
 * are owned here, not by the consumer.
 */
export function useToast() {
  return {
    toasts: readonly(toasts),
    push,
    info: (title: string, caption?: string) => push('info', title, { caption }),
    success: (title: string, caption?: string) => push('success', title, { caption }),
    warning: (title: string, caption?: string) => push('warning', title, { caption }),
    error: (title: string, caption?: string) => push('error', title, { caption }),
    dismiss,
  }
}
